import { API_CONFIG } from '../config/apiConfig';

// --- INTERFACES (Defines the shape of the data) ---

// Interface for the new summary API response
export interface FinancialSummary {
  savingsAndCashFlow: {
    totalAnnualIncome: number;
    totalAnnualExpenses: number;
    netAnnualSavings: number;
    savingRatio: number;
  };
  netWorth: {
    totalAssets: number;
    totalLiabilities: number;
    netWorth: number;
  };
  debtRatios: {
    totalMonthlyDebtPayments: number;
    debtServiceRatio: number;
    loanToAssetRatio: number;
  };
}

// --- AUTHENTICATION HELPERS ---

const getAuthToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('Authentication Error: JWT Token not found in localStorage.');
    return null;
  }
  return token;
};

// --- NEW UNIFIED API CALL ---

/**
 * Fetches the comprehensive financial summary object from the backend.
 * This includes calculated metrics for net worth, savings, and debt ratios.
 * @returns {Promise<FinancialSummary>} A promise that resolves to the summary object.
 */
export const fetchFinancialSummary = async (): Promise<FinancialSummary> => {
  const token = getAuthToken();
  if (!token) {
    // If there's no token, we can't make the call, so throw an error.
    throw new Error('Authentication token not found. Please log in.');
  }

  try {
    const response = await fetch(
      `${API_CONFIG.API_NODE_BASE_URL}/api/overview/summary`, // The new, single endpoint
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`, // Using the Bearer token auth method
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      // Try to get more detailed error info from the response body
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
      throw new Error(`HTTP error! Status: ${response.status} - ${errorData.message}`);
    }

    // Since the API returns the final object directly, we just parse the JSON
    const summaryData: FinancialSummary = await response.json();
    
    return summaryData;

  } catch (error) {
    console.error('Error fetching financial summary data:', error);
    // Re-throw the error so the component that calls this function can handle it
    throw error;
  }
};




















// import { API_CONFIG } from '../config/apiConfig';

// // --- INTERFACES (Defines the shape of the data) ---

// interface ApiResponseRow {
//   month?: string;
//   date?: string;
//   assets?: string;
//   liabilities?: string;
//   income?: string;
//   expense?: string;
//   coverage?: string;
//   progress?: string;
//   netIncome?: string;
//   grossRevenue?: string;
//   operatingExpenses?: string;
//   netInterestIncome?: string;
//   loansAndAdvances?: string;
//   customerDeposits?: string;
//   investment?: string;
// }

// // ✅ NEW: Interface for the new summary API response
// export interface FinancialSummary {
//   savingsAndCashFlow: {
//     totalAnnualIncome: number;
//     totalAnnualExpenses: number;
//     netAnnualSavings: number;
//     savingRatio: number;
//   };
//   netWorth: {
//     totalAssets: number;
//     totalLiabilities: number;
//     netWorth: number;
//   };
//   debtRatios: {
//     totalMonthlyDebtPayments: number;
//     debtServiceRatio: number;
//     loanToAssetRatio: number;
//   };
// }

// // --- AUTHENTICATION HELPERS (Your original code, unchanged) ---

// const getAuthToken = (): string | null => {
//   const token = localStorage.getItem('authToken');
//   if (!token) {
//     console.error('Authentication Error: JWT Token not found in localStorage.');
//     return null;
//   }
//   return token;
// };

// const getAuthHeader = (): string => {
//   const token = localStorage.getItem('authCredentials');
//   if (!token) {
//     throw new Error('User not logged in. No auth token found.');
//   }
//   return `Basic ${token}`;
// };


// // --- EXISTING API CALLS (Your original code, unchanged) ---

// // Asset vs Liability API call
// export const fetchAssetVsLiability = async (): Promise<{ name: string; assets: number; liabilities: number }[]> => {
//   const token = getAuthToken();
//   try {
//     const response = await fetch(
//       `${API_CONFIG.BASE_URL}/list/asset_vs_liability_list?startOffset=1&pageSize=10`,
//       {
//        method: 'GET',
//       headers: {
//         'Authorization': `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       },
//     });
    
//     if (!response.ok) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }
    
//     const data = await response.json();
//     return data.rows.map((row: ApiResponseRow) => ({
//       name: row.month || row.date || 'N/A',
//       assets: parseFloat(row.assets || '0') || 0,
//       liabilities: parseFloat(row.liabilities || '0') || 0
//     }));
//   } catch (error) {
//     console.error('Error fetching asset vs liability data:', error);
//     throw error;
//   }
// };

// // Income vs Expense API call
// export const fetchIncomeVsExpense = async (): Promise<{ name: string; income: number; expense: number }[]> => {
//   try {
//     const response = await fetch(
//       `${API_CONFIG.BASE_URL}/list/income_vs_expense`,
//       {
//         method: 'GET',
//         headers: {
//           'accept': 'application/json',
//           'api_id': API_CONFIG.API_ID,
//           'api_key': API_CONFIG.API_KEY,
//         },
//       }
//     );
    
//     if (!response.ok) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }
    
//     const data = await response.json();
//     return data.rows.map((row: ApiResponseRow) => ({
//       name: row.month || row.date || 'N/A',
//       income: parseFloat(row.income || '0') || 0,
//       expense: parseFloat(row.expense || '0') || 0
//     }));
//   } catch (error) {
//     console.error('Error fetching income vs expense data:', error);
//     throw error;
//   }
// };

// // Insurance API call
// export const fetchInsurance = async (): Promise<{ name: string; value: number }[]> => {
//   try {
//     const response = await fetch(
//       `${API_CONFIG.BASE_URL}/list/insurance_coverage`,
//       {
//         method: 'GET',
//         headers: {
//           'accept': 'application/json',
//           'api_id': API_CONFIG.API_ID,
//           'api_key': API_CONFIG.API_KEY,
//            'Authorization': getAuthHeader(),
//         },
//       }
//     );
    
//     if (!response.ok) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }
    
//     const data = await response.json();
//     return data.rows.map((row: ApiResponseRow) => ({
//       name: row.month || row.date || 'N/A',
//       value: parseFloat(row.coverage || '0') || 0
//     }));
//   } catch (error) {
//     console.error('Error fetching insurance data:', error);
//     throw error;
//   }
// };

// // ... (All your other fetch functions like fetchGoals, fetchNetIncome, etc. remain here, unchanged)


// // ✅ --- NEW FUNCTION ADDED HERE ---
// /**
//  * Fetches the comprehensive financial summary object from the backend.
//  * This includes calculated metrics for net worth, savings, and debt ratios.
//  * @returns {Promise<FinancialSummary>} A promise that resolves to the summary object.
//  */
// export const fetchFinancialSummary = async (): Promise<FinancialSummary> => {
//   const token = getAuthToken();
//   if (!token) {
//     // If there's no token, we can't make the call, so throw an error.
//     throw new Error('Authentication token not found. Please log in.');
//   }

//   try {
//     const response = await fetch(
//       `${API_CONFIG.BASE_URL}/api/overview/summary`, // The endpoint you provided
//       {
//         method: 'GET',
//         headers: {
//           'Authorization': `Bearer ${token}`, // Using the Bearer token auth method
//           'Content-Type': 'application/json',
//         },
//       }
//     );

//     if (!response.ok) {
//       // Try to get more detailed error info from the response body
//       const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
//       throw new Error(`HTTP error! Status: ${response.status} - ${errorData.message}`);
//     }

//     // Since the API returns the final object directly, we just parse the JSON
//     const summaryData: FinancialSummary = await response.json();
    
//     return summaryData;

//   } catch (error) {
//     console.error('Error fetching financial summary data:', error);
//     // Re-throw the error so the component that calls this function can handle it (e.g., show an error message)
//     throw error;
//   }
// };






